/**
 * DashboardMetricModal — detalle fila-a-fila detrás de una KPI card del
 * dashboard del terapeuta (DashboardOverview en TherapistPortal.tsx).
 *
 * Las 4 cards del overview ("Consultas este mes" / "Pacientes nuevos" /
 * "Consultas esta semana" / "Ingresos del mes") ya se veían clickeables
 * (cursor:pointer + hover) pero no hacían nada. Este modal cierra esa promesa:
 * al hacer click se abre una ventana con las consultas / pacientes concretos
 * que componen el número.
 *
 * Contexto B — Portal Terapeuta (tokens de TherapistPortal.css). ModalShell NO
 * se portalea, así que los custom properties del contexto resuelven acá dentro.
 *
 * Restricciones del proyecto:
 * - Sin console.log con datos de pacientes
 * - Sin servicios externos para avatares
 * - Sin tokens del Contexto A (modern-style.css)
 */

import React, { useEffect, useState } from 'react';
import ModalShell from '../ui/ModalShell';
import { SkeletonText } from '../ui/Skeleton';
import TherapistValidationService, {
  DashboardMetricKey,
  DashboardMetricDetail,
} from '../../services/therapist-validation.service';
import '../ui/metricDetailModal.css';

interface Props {
  therapistId: string;
  metric: DashboardMetricKey;
  onClose: () => void;
}

// ─── Metadatos por métrica ────────────────────────────────────────────────────

const META: Record<DashboardMetricKey, { title: string; accent: string }> = {
  consultations: { title: 'Consultas este mes',       accent: 'var(--apple-blue)' },
  newPatients:   { title: 'Pacientes nuevos este mes', accent: 'var(--apple-green)' },
  weekly:        { title: 'Consultas de esta semana',  accent: 'var(--apple-orange)' },
  revenue:       { title: 'Ingresos del mes',          accent: 'var(--apple-purple)' },
};

// ─── Helpers de formato ───────────────────────────────────────────────────────

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('es-CL', { day: 'numeric', month: 'short' });

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });

const formatCLP = (n: number) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency', currency: 'CLP', minimumFractionDigits: 0,
  }).format(n);

const TIPO_LABEL: Record<string, string> = {
  'Terapia Individual': 'Individual',
  'Terapia Individual - Sesión Extendida': 'Indiv. extendida',
  'Terapia Individual - Sesion Extendida': 'Indiv. extendida',
  'Terapia de Parejas': 'Parejas',
  'Terapia de Pareja': 'Parejas',
  'Terapia Familiar': 'Familiar',
  individual: 'Individual',
  pareja: 'Parejas',
  familiar: 'Familiar',
};
const tipoLabel = (t: string) => TIPO_LABEL[t] ?? t;

const MODALIDAD_LABEL: Record<string, string> = {
  online: 'Online',
  presencial: 'Presencial',
  domicilio: 'A domicilio',
};
const modalidadLabel = (m: string) =>
  MODALIDAD_LABEL[m] ?? (m ? m[0].toUpperCase() + m.slice(1) : '');

const ESTADO_META: Record<string, { label: string; tone: 'blue' | 'green' | 'orange' }> = {
  agendada:   { label: 'Agendada',   tone: 'blue' },
  programada: { label: 'Programada', tone: 'blue' },
  confirmada: { label: 'Confirmada', tone: 'blue' },
  completada: { label: 'Completada', tone: 'green' },
};
const estadoMeta = (e: string) => ESTADO_META[e] ?? { label: e, tone: 'blue' as const };

// estado_pago_consulta → etiqueta corta para la fila de valor (null = sin dato relevante)
const PAGO_LABEL: Record<string, string> = {
  anticipo_pendiente: 'anticipo pendiente',
  anticipo_pagado: 'anticipo pagado',
  pago_completo: 'pagado',
};
const pagoLabel = (e: string): string | null => PAGO_LABEL[e] ?? null;

// ─── Filas ────────────────────────────────────────────────────────────────────

const ConsultaItem: React.FC<{ c: DashboardMetricDetail['consultas'][number] }> = ({ c }) => {
  const est = estadoMeta(c.estado);
  const pago = pagoLabel(c.estado_pago_consulta);
  return (
    <li className="mdm-item">
      <div className="mdm-item__top">
        <span className="mdm-item__name">{c.paciente_nombre}</span>
        <span className={`mdm-badge mdm-badge--${est.tone}`}>{est.label}</span>
      </div>
      <div className="mdm-item__meta">
        {formatDate(c.fecha_hora)} · {formatTime(c.fecha_hora)} · {c.duracion} min
        {' · '}{tipoLabel(c.tipo_consulta)}
        {c.modalidad && <> · {modalidadLabel(c.modalidad)}</>}
      </div>
      <div className="mdm-item__value">
        {formatCLP(c.valor_final)}
        {pago && <span className="mdm-item__pago"> · {pago}</span>}
      </div>
    </li>
  );
};

// ─── Cuerpo según métrica ─────────────────────────────────────────────────────

const Body: React.FC<{ detail: DashboardMetricDetail }> = ({ detail }) => {
  const { metric } = detail;

  if (metric === 'newPatients') {
    if (detail.newPatients.length === 0) {
      return <p className="mdm-empty">Todavía no hay pacientes nuevos este mes.</p>;
    }
    return (
      <>
        <p className="mdm-count">
          {detail.newPatients.length} {detail.newPatients.length === 1 ? 'paciente nuevo' : 'pacientes nuevos'}
          <span className="mdm-count__hint"> · sin consulta previa con vos antes del día 1</span>
        </p>
        <ul className="mdm-list">
          {detail.newPatients.map(p => (
            <li key={p.id} className="mdm-item">
              <div className="mdm-item__top">
                <span className="mdm-item__name">{p.nombre}</span>
              </div>
              <div className="mdm-item__meta">
                Primera consulta: {formatDate(p.primera_consulta_fecha)} · {tipoLabel(p.tipo_consulta)}
              </div>
            </li>
          ))}
        </ul>
      </>
    );
  }

  // consultations · weekly · revenue → lista de consultas
  const { consultas } = detail;

  if (consultas.length === 0) {
    return (
      <p className="mdm-empty">
        {metric === 'weekly'
          ? 'No hay consultas agendadas para esta semana.'
          : 'Todavía no hay consultas este mes.'}
      </p>
    );
  }

  return (
    <>
      {metric === 'revenue' && (
        <div className="mdm-revenue">
          <div className="mdm-revenue__cell">
            <span className="mdm-revenue__label">Realizado</span>
            <span className="mdm-revenue__value mdm-revenue__value--ok">
              {formatCLP(detail.revenue.realizado)}
            </span>
            <span className="mdm-revenue__hint">consultas completadas</span>
          </div>
          <div className="mdm-revenue__cell">
            <span className="mdm-revenue__label">Proyectado</span>
            <span className="mdm-revenue__value">{formatCLP(detail.revenue.proyectado)}</span>
            <span className="mdm-revenue__hint">agendadas aún no realizadas</span>
          </div>
          <div className="mdm-revenue__cell mdm-revenue__cell--total-purple">
            <span className="mdm-revenue__label">Total del mes</span>
            <span className="mdm-revenue__value">{formatCLP(detail.revenue.total)}</span>
          </div>
        </div>
      )}

      <p className="mdm-count">
        {consultas.length} {consultas.length === 1 ? 'consulta' : 'consultas'}
        {metric === 'weekly' && detail.rangeLabel && (
          <span className="mdm-count__hint"> · {detail.rangeLabel}</span>
        )}
      </p>
      <ul className="mdm-list">
        {consultas.map(c => <ConsultaItem key={c.id} c={c} />)}
      </ul>
    </>
  );
};

// ─── Componente ───────────────────────────────────────────────────────────────

const DashboardMetricModal: React.FC<Props> = ({ therapistId, metric, onClose }) => {
  const [detail, setDetail] = useState<DashboardMetricDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    TherapistValidationService.getDashboardMetricDetail(therapistId, metric)
      .then(d => { if (alive) setDetail(d); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [therapistId, metric]);

  const meta = META[metric];
  const title = (
    <span className="mdm-title">
      {meta.title}
      {detail?.rangeLabel && metric !== 'weekly' && (
        <span className="mdm-title__range"> · {detail.rangeLabel}</span>
      )}
    </span>
  );

  return (
    <ModalShell title={title} onClose={onClose} size="lg" accentColor={meta.accent}>
      {loading && (
        <div className="mdm-loading">
          <SkeletonText lines={5} />
        </div>
      )}
      {!loading && error && (
        <p className="mdm-empty">
          No se pudo cargar el detalle. Cerrá y volvé a intentar.
        </p>
      )}
      {!loading && !error && detail && <Body detail={detail} />}
    </ModalShell>
  );
};

export default DashboardMetricModal;
